<template>
    <div>
        <v-toolbar flat dense class="ma-2">
            <v-toolbar-title class="subtitle-1">
                {{ lista.length }} pod(s) · {{ totalMemoria }} instância(s) em memória
            </v-toolbar-title>

            <v-spacer/>

            <span v-if="lidoEm" class="caption grey--text mr-3">lido {{ lidoHa }}</span>

            <v-btn icon :loading="loading" title="Reler os pods" @click="carregar(true)">
                <v-icon>mdi-refresh</v-icon>
            </v-btn>

            <v-btn v-if="podeEditar" color="primary" small class="ml-2" @click="$refs.dialog.abrir()">
                <v-icon left small>mdi-plus</v-icon>
                Novo pod
            </v-btn>
        </v-toolbar>

        <v-row dense class="ma-1">
            <v-col v-for="pod in lista" :key="pod.id" cols="12" sm="6" md="4" lg="3">
                <v-card :class="{ 'grey lighten-4': !pod.ativo }" outlined>
                    <div class="d-flex align-start px-4 pt-3 pb-1">
                        <v-icon small :color="corDoPod(pod)" class="mr-2 mt-1">mdi-circle</v-icon>

                        <div class="cabecalho">
                            <div class="subtitle-2 text-truncate" :title="pod.nome">{{ pod.nome }}</div>
                            <div class="caption grey--text text-truncate">
                                {{ pod.endereco }}<span v-if="!pod.ativo"> · inativo</span>
                            </div>
                        </div>

                        <v-menu v-if="podeEditar || podeMover" offset-y>
                            <template v-slot:activator="{ on }">
                                <v-btn icon x-small v-on="on"><v-icon small>mdi-dots-vertical</v-icon></v-btn>
                            </template>
                            <v-list dense>
                                <v-list-item v-if="podeEditar" @click="$refs.dialog.abrir(pod)">
                                    <v-list-item-title>Editar</v-list-item-title>
                                </v-list-item>
                                <v-list-item v-if="podeMover" @click="reconciliar(pod)">
                                    <v-list-item-title>Reconciliar</v-list-item-title>
                                </v-list-item>
                                <v-list-item @click="$emit('ver-instancias', pod)">
                                    <v-list-item-title>Ver instâncias</v-list-item-title>
                                </v-list-item>
                                <v-list-item v-if="podeEditar" @click="excluir(pod)">
                                    <v-list-item-title class="red--text">Remover</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </div>

                    <v-card-text class="pt-2">
                        <div v-if="pod.respondeu === false" class="body-2 grey--text">
                            Sem resposta
                            <div class="caption">
                                O processo não respondeu. Não se sabe nada sobre as instâncias dele.
                            </div>
                        </div>

                        <template v-else>
                            <div class="d-flex justify-space-between body-2">
                                <span class="grey--text">Linhas no banco</span>
                                <span class="grey--text">{{ pod.no_banco }}</span>
                            </div>
                            <div class="d-flex justify-space-between body-2">
                                <span>Sessões em memória</span>
                                <strong>{{ pod.na_memoria }}</strong>
                            </div>
                            <div class="d-flex justify-space-between body-2">
                                <span>Online</span>
                                <strong :class="{ 'orange--text': pod.online < pod.na_memoria }">
                                    {{ pod.online }}
                                </strong>
                            </div>

                            <v-divider class="my-2"/>

                            <div v-if="pod.info" class="caption grey--text">
                                <div>WA {{ pod.info.wa_version || '—' }} · {{ pod.info.rss_mb }} MB</div>
                                <div>no ar há {{ uptime(pod.info.uptime_s) }}</div>
                                <div v-if="pod.info.browser">{{ pod.info.browser.filter(Boolean).join(' / ') }}</div>
                                <div v-if="pod.info.proxy" class="green--text">proxy BR ativo</div>
                            </div>

                            <v-alert
                                v-if="pod.identidade_confere === false"
                                dense
                                text
                                type="error"
                                class="caption mt-2 mb-0"
                            >
                                O pod se identifica como {{ pod.info && pod.info.host }}:{{ pod.info && pod.info.port }}.
                                Com HOST errado ele restaura o conjunto errado de sessões no restart.
                            </v-alert>

                            <v-alert
                                v-else-if="pod.fora_do_lugar"
                                dense
                                text
                                type="error"
                                class="caption mt-2 mb-0"
                            >
                                {{ pod.fora_do_lugar }} instância(s) deste pod estão vivas em outro:
                                o envio delas bate no endereço errado. "Reconciliar" acerta sem reiniciar container.
                            </v-alert>

                            <v-alert
                                v-else-if="pod.intrusas"
                                dense
                                text
                                type="warning"
                                class="caption mt-2 mb-0"
                            >
                                {{ pod.intrusas }} sessão(ões) carregada(s) aqui pertencem a outro pod
                                (ou sumiram do banco). "Reconciliar" descarrega sem apagar nada.
                            </v-alert>
                        </template>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <PodDialog ref="dialog" @salvo="carregar(true)"/>
    </div>
</template>

<script>
import PodDialog from './PodDialog';
import { carregarPods, pods } from './pods';

export default {
    name: 'TabPods',

    components: { PodDialog },

    data: () => ({
        loading: false,
        lista: [],
        lidoEm: null,
    }),

    mounted() {
        this.carregar(true);
    },

    computed: {
        podeEditar() {
            return this.checkPermission('zapercontrol_instancias_pods');
        },

        podeMover() {
            return this.checkPermission('zapercontrol_instancias_mover');
        },

        totalMemoria() {
            return this.lista.reduce((soma, p) => soma + (p.na_memoria || 0), 0);
        },

        lidoHa() {
            return this.lidoEm ? this.moment(this.lidoEm).fromNow() : '';
        },
    },

    methods: {
        async carregar(atualizar = false) {
            this.loading = true;

            try {
                await carregarPods(this.$http, true, atualizar);
                this.lista = pods();
                this.lidoEm = new Date();
            } catch (e) {
                this.notify('Não foi possível carregar os pods', 'error');
            }

            this.loading = false;
        },

        corDoPod(pod) {
            if (!pod.ativo) return 'grey';
            if (pod.respondeu === false) return 'grey darken-1';
            if (pod.identidade_confere === false) return 'red';
            if (pod.fora_do_lugar) return 'red';
            if (pod.intrusas) return 'amber darken-2';
            return 'green';
        },

        uptime(segundos) {
            if (!segundos) return '—';

            const dias = Math.floor(segundos / 86400);
            const horas = Math.floor((segundos % 86400) / 3600);

            return dias ? `${dias}d ${horas}h` : `${horas}h`;
        },

        async reconciliar(pod) {
            this.loading = true;

            try {
                const { data } = await this.$http.post(`instancias-whatsapp/pods/${pod.id}/reconciliar`);

                if (data.type !== 'success') {
                    this.notify(data.msg, 'warning');
                    this.loading = false;
                    return;
                }

                const plano = data.data.plano;

                if (!plano.carregar.length && !plano.descarregar.length) {
                    this.notify('Banco e memória já estão iguais neste pod', 'success');
                    this.loading = false;
                    return;
                }

                const ok = await this.$root.$confirm(
                    `Reconciliar ${pod.nome}`,
                    `Carregar ${plano.carregar.length} sessão(ões) que o banco manda para este pod e `
                    + `descarregar ${plano.descarregar.length} que não pertencem a ele. `
                    + 'Nenhuma sessão é apagada.'
                );

                if (!ok) {
                    this.loading = false;
                    return;
                }

                const { data: resposta } = await this.$http.post(
                    `instancias-whatsapp/pods/${pod.id}/reconciliar?aplicar=1`
                );

                const r = resposta.data.resultado;

                this.notify(
                    `${r.carregadas} carregada(s), ${r.descarregadas} descarregada(s)`
                    + (r.erros.length ? `, ${r.erros.length} erro(s)` : ''),
                    r.erros.length ? 'warning' : 'success'
                );

                this.carregar(true);
            } catch (e) {
                this.notify('Não foi possível reconciliar', 'error');
            }

            this.loading = false;
        },

        async excluir(pod) {
            const ok = await this.$root.$confirm(
                'Remover pod',
                `${pod.nome} sai do registro. O processo continua rodando no servidor — isto só tira `
                + 'o endereço da lista de destinos.'
            );

            if (!ok) return;

            try {
                const { data } = await this.$http.delete(`instancias-whatsapp/pods/${pod.id}`);

                this.notify(data.msg || 'Pod removido', data.type || 'success');
                this.carregar(true);
            } catch (e) {
                this.notify('Não foi possível remover', 'error');
            }
        },
    },
};
</script>

<style scoped>
.cabecalho {
    flex: 1 1 auto;
    min-width: 0;
}
</style>
